import { Link } from '#components/atoms/link/Link.tsx';
import { Snippet } from '#components/patterns/code/snippet/Snippet.tsx';
import type { ClientInfo } from '#dn-types/downloads.ts';
import type { HostNoun } from '#dn-types/host.ts';
import type { EnrollmentState } from '../types.ts';
import { BINARY_SETUP_COMMANDS } from './binary-install.ts';
import { LinuxArchSelect } from './LinuxArchSelect.tsx';
import { PACKAGE_INSTALL_COMMANDS, type LinuxInstallMethod } from './package-install.ts';
import { CondenseButton } from './token-instructions/CondenseButton.tsx';
import { TokenInstructions } from './token-instructions/TokenInstructions.tsx';

type Props = {
  hostNoun: HostNoun;
  enrollmentState: EnrollmentState;
  downloadCommand?: string;
  /** Linux only: options + selection for the architecture picker inside the binary-download step. */
  archOptions?: ClientInfo[];
  arch?: string;
  onArchChange?: (arch: string) => void;
  /** Which install path to show; the install-method picker itself is laid out by the parent (packages are Linux-only). */
  installMethod?: LinuxInstallMethod;
  onCollapse: () => void;
};

export function LinuxInstructions({
  hostNoun,
  enrollmentState,
  downloadCommand,
  archOptions,
  arch,
  onArchChange,
  installMethod = 'binary',
  onCollapse,
}: Props) {
  const collapse = <CondenseButton onClick={onCollapse} />;
  const packageManager = installMethod === 'binary' ? undefined : installMethod;

  return (
    <>
      {packageManager ?
        <>
          <h2 className="h3">3. Install dnclient</h2>
          {packageManager === 'aur' ?
            <p className="p-md">
              <code>dnclient</code> is available{' '}
              <a href="https://aur.archlinux.org/packages/dnclient" className="a" target="_blank" rel="noreferrer">
                in the AUR
              </a>
              . You know what to do.
            </p>
          : <p className="p-md">
              Run these commands on the {hostNoun} to add Defined Networking’s{' '}
              {packageManager === 'apt' ? 'APT' : 'RPM'} repository, then install and start the <code>dnclient</code>{' '}
              service. Future updates arrive through your regular package upgrades.
            </p>
          }
          <Snippet className="mb-40" breakWord={true} secondaryButton={collapse}>
            {PACKAGE_INSTALL_COMMANDS[packageManager]}
          </Snippet>
          <h2 className="h3">4. Enroll {hostNoun}</h2>
          <TokenInstructions
            hostNoun={hostNoun}
            enrollmentState={enrollmentState}
            onCollapse={onCollapse}
            platform="Linux"
            installMethod={packageManager}
          />
        </>
      : <>
          <h2 className="h3">3. Download dnclient</h2>
          {onArchChange && archOptions ?
            <LinuxArchSelect className="mb-24" archOptions={archOptions} arch={arch} onArchChange={onArchChange} />
          : null}
          {downloadCommand ?
            <>
              <p className="p-md">
                Run the provided <code>curl</code> command directly on the {hostNoun}.
              </p>
              <Snippet className="mb-40" breakWord={true} secondaryButton={collapse}>
                {downloadCommand}
              </Snippet>
            </>
          : <p className="p-md">
              Download dnclient from the <Link to="/downloads">Downloads</Link> page and move it to the {hostNoun} being
              enrolled.
            </p>
          }
          <h2 className="h3">4. Make dnclient executable</h2>
          <Snippet className="mb-40" secondaryButton={collapse}>
            {BINARY_SETUP_COMMANDS.makeExecutable}
          </Snippet>
          <h2 className="h3">5. Install dnclient service</h2>
          <p className="p-md mb-24">
            This will setup a service that uses the dnclient binary in its current directory.
          </p>
          <Snippet className="mb-40" secondaryButton={collapse}>
            {BINARY_SETUP_COMMANDS.installService}
          </Snippet>
          <h2 className="h3">6. Start dnclient service</h2>
          <p className="p-md mb-24">The client must be started before you can enroll the {hostNoun}.</p>
          <Snippet className="mb-40" secondaryButton={collapse}>
            {BINARY_SETUP_COMMANDS.startService}
          </Snippet>
          <h2 className="h3">7. Enroll {hostNoun}</h2>
          <TokenInstructions
            hostNoun={hostNoun}
            enrollmentState={enrollmentState}
            onCollapse={onCollapse}
            platform="Linux"
          />
        </>
      }
    </>
  );
}
